import { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import { sxAutocomplete, sxButton } from "./sxProps";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { TypeScheduleTabsEnum } from "../../../interfaces/enum/TypeScheduleTabsEnum";
import "./styles.css";
import { LocalDeExamesProps } from "../../../interfaces/props/SaudeOnline/LocalDeExamesProps";
import CitiesListModal from "./CitiesListModal";
import LoadingApp from "../../../components/LoadingApp";
import {
  getCidadeExecucaoExame,
  getEstadoExecucaoExame,
} from "../../../services/SaudeOnlineService";
import { City } from "../../../interfaces/entities/City";
import { States } from "../../../interfaces/entities/States";
import { useAccessibility } from "../../../Store/contexts/AccessibilityContext";
import { getStateFullName } from "../../../utils/StateMap";

function LocalDeExames(props: LocalDeExamesProps) {
  const [area, setArea] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const [showLoading, setShowLoading] = useState<boolean>(true);
  const [closeCities, setCloseCities] = useState<City[]>([]);
  const [city, setCity] = useState<City | null>(null);
  const [states, setStates] = useState<States[]>([]);
  const [selectedState, setSelectedState] = useState<States | null>(null);
  const [distantCities, setDistantCities] = useState<City[]>([]);
  const [distantCity, setDistantCity] = useState<City | null>(null);
  const [allowProceed, setAllowProceed] = useState<boolean>(false);

  const accessibility = useAccessibility();

  useEffect(() => {
    const getCities = async () => {
      const result = await getCidadeExecucaoExame("", "");
      if (result !== null) {
        setCloseCities(result);
        getUf();
      }
    };

    const getUf = async () => {
      const result = await getEstadoExecucaoExame();
      if (result !== null) {
        setStates(result);
        setShowLoading(false);
      }
    };

    getCities();
  }, []);

  useEffect(() => {
    if (distantCity !== null || city !== null) {
      setAllowProceed(true);
    } else {
      setAllowProceed(false);
    }
  }, [distantCity, city]);

  const goBack = () => {
    props.backStep(TypeScheduleTabsEnum.Horario, false);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === "withinArea") {
      setAllowProceed(true);
    } else {
      setAllowProceed(false);
    }
    setArea(event.target.value);
    setCity(null);
    setDistantCity(null);
    setSelectedState(null);
  };

  const handleProceed = () => {
    if (city !== null) {
      props.handleLocalDeExames(city);
    } else if (distantCity !== null) {
      props.handleLocalDeExames(distantCity);
    }
    props.handleLocalDeExames();
  };

  const openCitiesListModal = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCity = (selectedCity: City) => {
    setCity(selectedCity);
    setOpen(false);
  };

  const handleUf = async (
    event: React.SyntheticEvent,
    value: States | null
  ) => {
    setSelectedState(value);
    setDistantCity(null);
    if (value !== null) {
      const result = await getCidadeExecucaoExame(`${value.idUf}`, "");

      if (result !== null) {
        setDistantCities(result);
      }
    } else {
      setDistantCities([]);
    }
  };

  const handleCityChange = (
    event: React.SyntheticEvent,
    value: City | null
  ) => {
    setDistantCity(value);
  };

  return (
    <div className="terms-of-use-holder">
      <LoadingApp showLoading={showLoading} />
      <h1
        className="terms-title"
        style={accessibility.textStyleAccessibility}
      >
        Selecione o local da realização dos exames:
      </h1>
      <div className="exam-location-options-holder">
        <RadioGroup
          aria-labelledby="demo-radio-buttons-group-label"
          name="radio-buttons-group"
          value={area}
          onChange={handleChange}
        >
          <FormControlLabel
            value={"withinArea"}
            control={
              <Radio
                style={accessibility.textStyleAccessibility}
                size="small"
              />
            }
            label={
              <span
                className="location-radio-button"
                style={accessibility.textStyleAccessibility}
              >
                Cidade <b>na área de cobertura</b> da <br />
                Unimed Campinas
              </span>
            }
          />
          <button
            className="close-cities-list-button"
            disabled={area !== "withinArea"}
            onClick={openCitiesListModal}
            style={accessibility.textStyleAccessibility}
          >
            [Lista de cidades]
          </button>
          <FormControlLabel
            value={"outsideArea"}
            control={
              <Radio
                style={accessibility.textStyleAccessibility}
                size="small"
              />
            }
              label={
              <span
                className="location-radio-button"
                style={accessibility.textStyleAccessibility}
              >
                Cidade <b>fora da área de cobertura</b> da <br />
                Unimed Campinas
              </span>
            }
          />
        </RadioGroup>
      </div>
      {area === "outsideArea" && (
        <div className="distant-cities-holder">
          <div className="cities-autocomplete">
            <h1
              className="hour-title"
              style={accessibility.textStyleAccessibility}
            >
              Estado{" "}
            </h1>
            <label htmlFor="estado" aria-label="estado" />
            <Autocomplete
              disablePortal
              fullWidth
              id="estado"
              onChange={handleUf}
              options={states}
              value={selectedState}
              getOptionLabel={(option: States) => option.siglaUf}
              sx={sxAutocomplete}
              renderInput={(params) => <TextField {...params} />}
              renderOption={(props, option) => (
                <li {...props} aria-label={getStateFullName(option.siglaUf) || ''}>
                  {option.siglaUf} 
                </li>
              )}
            />
          </div>
          <div className="cities-autocomplete">
            <h1
              className="hour-title"
              style={accessibility.textStyleAccessibility}
            >
              Cidade{" "}
            </h1>
            <label htmlFor="cidade" aria-label="cidade" />
            <Autocomplete
              disablePortal
              fullWidth
              id="cidade"
              onChange={handleCityChange}
              options={distantCities}
              value={distantCity}
              getOptionLabel={(option: City) => option.descr}
              sx={sxAutocomplete}
              renderInput={(params) => <TextField {...params} />}
            />
          </div>
        </div>
      )}
      <div className="location-buttons">
        <Button
          variant="contained"
          size="large"
          onClick={handleProceed}
          sx={sxButton}
          style={accessibility.buttonStyleAccessibility}
          disabled={!allowProceed}
          fullWidth
        >
          <span style={accessibility.buttonTextStyleAccessibility}>
            PROSSEGUIR
          </span>
        </Button>
      </div>
      <button
        className="back-button"
        onClick={goBack}
        style={accessibility.textStyleAccessibility}
        aria-label="Voltar para seleção de horário"
      >
        <FontAwesomeIcon
          style={accessibility.textStyleAccessibility}
          icon={faChevronLeft}
        />
        &nbsp;&nbsp;<u>Voltar</u>
      </button>
      <CitiesListModal
        open={open}
        handleClose={handleClose}
        handleCity={handleCity}
        closeCities={closeCities}
      />
    </div>
  );
}

export default LocalDeExames;
